import React, { useEffect, useState, forwardRef, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Slide,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
  TextField,
  Grid,
  DialogActions,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import dayjs from 'dayjs';
import validationRules, {
  maxLengthValidation,
} from '../../utils/validations.util';
import Iconify from '../common/iconify.component';
import Loading from '../table-elements/loading.component';
import SnackbarInfo from '../common/snackbar-info.component';
import Status from '../form-elements/status.component';
import Datetimepicker from '../form-elements/datetimepicker.component';
import {
  convertDateStringToTimestamp,
  getCurrentTimestamp,
} from '../../utils/datetime.util';
import {
  createNewData,
  updateDataById,
  viewDataById,
} from '../../services/quiz.service';
import { QUIZ_CONFIG } from '../../config/const.config';

const Transition = forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

const AddEditQuiz = ({ title, dataId, onCancel, onSuccess }) => {
  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      title: '',
      description: '',
      passCriteria: 0,
      allowedAttempts: 1,
      showAnswersFromAttempt: null,
      startTime: null,
      endTime: null,
      status: 1,
    },
  });

  const watchAllowedAttempts = watch('allowedAttempts');
  const [loading, setLoading] = useState(true);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });
  const quizStartTimeRef = useRef(null);

  const saveNewData = (payload) => {
    createNewData(payload)
      .then(() => {
        onSuccess('New quiz created successfully.');
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
      });
  };

  const updateExistingData = (payload) => {
    updateDataById(dataId, payload)
      .then(() => {
        onSuccess('Quiz details updated successfully.');
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
      });
  };

  const onFormSubmit = (data) => {
    setFormSubmitted(true);
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    const startDateTimestamp = convertDateStringToTimestamp(data.startTime);
    const endDateTimestamp = convertDateStringToTimestamp(data.endTime);
    const currentTime = getCurrentTimestamp();

    if (
      startDateTimestamp <= currentTime &&
      startDateTimestamp !== quizStartTimeRef.current
    ) {
      setTimeout(() => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message: 'Quiz start date must be greater than current time.',
        });
      }, 100);
      setFormSubmitted(false);
      return;
    }

    const payload = {
      title: data.title,
      description: data.description,
      passCriteria: data.passCriteria || 0,
      allowedAttempts: data.allowedAttempts || 1,
      showAnswersFromAttempt: data.showAnswersFromAttempt ?? null,
      startTime: startDateTimestamp,
      endTime: endDateTimestamp,
      status: Number(data.status),
    };

    if (dataId) {
      updateExistingData(payload);
    } else {
      saveNewData(payload);
    }
  };

  useEffect(() => {
    if (!dataId) {
      setLoading(false);
      return;
    }

    viewDataById(dataId)
      .then((res) => {
        // Fill form values
        quizStartTimeRef.current = res.data?.startTime;
        setValue('title', res.data?.title || '');
        setValue('description', res.data?.description || '');
        setValue('passCriteria', Number(res.data?.passCriteria) || 0);
        setValue('allowedAttempts', Number(res.data?.allowedAttempts) || 1);
        setValue(
          'showAnswersFromAttempt',
          Number(res.data?.showAnswersFromAttempt) ?? null
        );
        setValue(
          'startTime',
          res.data?.startTime ? dayjs.unix(res.data?.startTime) : null
        );
        setValue(
          'endTime',
          res.data?.endTime ? dayjs.unix(res.data?.endTime) : null
        );
        setValue('status', res.data?.status);

        setLoading(false);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setLoading(false);
      });
  }, [dataId]);

  return (
    <Dialog
      open
      aria-labelledby="add-dialog-title"
      aria-describedby="add-dialog-description"
      TransitionComponent={Transition}
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle id="add-dialog-title">{title}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onCancel}
        size="small"
        sx={{
          position: 'absolute',
          right: 10,
          top: 15,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <Iconify icon="ic:outline-close" />
      </IconButton>

      <DialogContent dividers id="add-dialog-description">
        {loading && <Loading />}

        {!loading && (
          <form id="add-edit-form" onSubmit={handleSubmit(onFormSubmit)}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="title"
                  control={control}
                  rules={{
                    ...validationRules.REQUIRED,
                    ...maxLengthValidation(255),
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Title"
                      type="text"
                      autoComplete="off"
                      error={!!errors.title}
                      helperText={errors?.title?.message || null}
                      fullWidth
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={12}>
                <Controller
                  name="description"
                  control={control}
                  rules={{
                    ...validationRules.REQUIRED,
                    ...maxLengthValidation(1500),
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Description"
                      type="text"
                      autoComplete="off"
                      error={!!errors.description}
                      helperText={errors?.description?.message || null}
                      fullWidth
                      multiline
                      rows={3}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="passCriteria"
                  control={control}
                  rules={{
                    ...validationRules.REQUIRED_NUMBER,
                    ...maxLengthValidation(3),
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Passing Criteria (in %)"
                      name="passCriteria"
                      type="number"
                      error={!!errors.passCriteria}
                      helperText={errors?.passCriteria?.message || null}
                      fullWidth
                      inputProps={{ inputMode: 'numeric', min: 0, max: 100 }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="status"
                  control={control}
                  rules={validationRules.REQUIRED}
                  render={({ field: { onChange, value } }) => (
                    <Status
                      id="quiz_status"
                      name="quiz_status"
                      label="Status"
                      defaultValue={value}
                      onChange={onChange}
                      validOptions={[1, 0]}
                      sx={{ width: '100%' }}
                      error={errors?.status?.message || ''}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="allowedAttempts"
                  control={control}
                  rules={{
                    ...validationRules.REQUIRED_NUMBER,
                    ...maxLengthValidation(2),
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Attempts Allowed"
                      name="allowedAttempts"
                      type="number"
                      error={!!errors.allowedAttempts}
                      helperText={
                        errors?.allowedAttempts?.message ||
                        `Max ${QUIZ_CONFIG.MAX_ALLOWED_ATTEMPTS} Attempts`
                      }
                      fullWidth
                      inputProps={{
                        inputMode: 'numeric',
                        min: 1,
                        max: QUIZ_CONFIG.MAX_ALLOWED_ATTEMPTS,
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="showAnswersFromAttempt"
                  control={control}
                  rules={{
                    ...validationRules.OPTIONAL_NUMBER,
                    ...maxLengthValidation(2),
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Show Answers from Attempt"
                      name="showAnswersFromAttempt"
                      type="number"
                      error={!!errors.showAnswersFromAttempt}
                      helperText={
                        errors?.showAnswersFromAttempt?.message ||
                        "Pass '0' or keep blank to not show answers."
                      }
                      fullWidth
                      inputProps={{
                        inputMode: 'numeric',
                        max: Math.min(
                          watchAllowedAttempts ||
                            QUIZ_CONFIG.MAX_ALLOWED_ATTEMPTS,
                          QUIZ_CONFIG.MAX_ALLOWED_ATTEMPTS
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="startTime"
                  control={control}
                  rules={validationRules.REQUIRED}
                  render={({ field: { onChange, value } }) => (
                    <Datetimepicker
                      id="startTime"
                      name="startTime"
                      label="Quiz Start Date"
                      value={value}
                      defaultValue={!dataId ? null : value}
                      onChange={onChange}
                      sx={{ width: '100%' }}
                      error={errors?.startTime?.message || ''}
                      disablePast
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="endTime"
                  control={control}
                  rules={validationRules.REQUIRED}
                  render={({ field: { onChange, value } }) => (
                    <Datetimepicker
                      id="endTime"
                      name="endTime"
                      label="Quiz End Date"
                      value={value}
                      defaultValue={!dataId ? null : value}
                      onChange={onChange}
                      sx={{ width: '100%' }}
                      error={errors?.endTime?.message || ''}
                      disablePast
                    />
                  )}
                />
              </Grid>
            </Grid>
          </form>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'left', marginLeft: 2 }}>
        <Button
          type="submit"
          form="add-edit-form"
          color="primary"
          variant="contained"
          disabled={loading || formSubmitted}
        >
          Save
        </Button>
        <Button
          color="secondary"
          variant="contained"
          onClick={onCancel}
          disabled={formSubmitted}
        >
          Cancel
        </Button>
      </DialogActions>

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </Dialog>
  );
};

AddEditQuiz.propTypes = {
  title: PropTypes.string.isRequired,
  dataId: PropTypes.number.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default AddEditQuiz;
